// =============================================================================
// AR / Invoices dashboard — shared constants + pure helpers.
// Ported from the ar-due prototype (Documents/ar-due_2026_AUG_05.html): the
// aging buckets, plan labels, payment lenses, badge palettes, match-priority
// roll-up and the paid-detection math (mirrors the backend's max($0.05, 0.1%)
// tolerance). Pure module — no React, no fetch.
// =============================================================================
import { csvCell } from "../utils/csvCell";

export const money = (v, dash = "—") =>
  v == null || isNaN(v) ? dash : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Aging buckets — amounts shown are Remaining (net of posted payments).
export const BUCKETS = {
  today:   { label: "Due today",  sub: "due today · still owed", fromDays: 0,     toDays: 0 },
  week:    { label: "This week",  sub: "due in 1–7 days",        fromDays: 1,     toDays: 7 },
  month:   { label: "This month", sub: "due in 8–30 days",       fromDays: 8,     toDays: 30 },
  overdue: { label: "Overdue",    sub: "past due · chase now",   fromDays: -3650, toDays: -1 },
};

export const PLANS = {
  ALL:   { label: "All plans", cat: null },
  CASH:  { label: "N30",  cat: "CASH" },
  SPLIT: { label: "BK",   cat: "SPLIT" },
  POT:   { label: "PoT",  cat: "POT" },
  CTO:   { label: "CTO",  cat: "CTO" },
};

export const PAYFILTERS = {
  owed: { label: "Owed",         sub: "still to collect" },
  paid: { label: "Paid / ahead", sub: "already covered" },
  all:  { label: "All",          sub: "everything due" },
};

export const GROUPBYS = {
  invoice:   { label: "Invoice" },
  hospital:  { label: "Hospital" },
  rep:       { label: "Sales rep" },
  territory: { label: "Territory" },
};

// Badge palettes (bootstrap variants) per status value.
export const MATCH_BADGE = {
  Matched: "success", PartialOpen: "warning", Short: "danger", Over: "danger",
  Unchecked: "secondary", NoCharge: "secondary", CapBreach: "danger", DuplicatePO: "danger",
};
export const SEND_BADGE = { Sent: "success", Queued: "info", Failed: "danger", NotSent: "secondary" };
export const PAID_BADGE = { Paid: "success", PartiallyPaid: "warning", Unpaid: "secondary", Unknown: "secondary" };
export const POST_BADGE = { Sent: "success", Validated: "info", PoUploaded: "warning", AwaitingPO: "secondary", AwaitingPo: "secondary" };

// Worst-first roll-up for a group's match histogram.
export const MATCH_PRIORITY = ["CapBreach", "DuplicatePO", "Over", "Short", "PartialOpen", "Unchecked", "NoCharge", "Matched"];
export const worstMatch = (statuses) =>
  MATCH_PRIORITY.find((s) => statuses.includes(s)) || null;
// Only problem verdicts surface on list rows; clean matches stay quiet.
export const PROBLEM_MATCHES = ["CapBreach", "DuplicatePO", "Over", "Short"];

// ── Paid-detection math (mirrors the backend's max($0.05, 0.1%)) ────────────
export const remOf = (it) => (it.remaining ?? it.amount ?? 0);
export const tolOf = (it) => Math.max(0.05, 0.001 * (it.amount || 0));
export const isPaidItem = (it) => remOf(it) <= tolOf(it);
// Territory = the rep-code prefix before "-R" (e.g. "WD40-R19" → "WD40").
export const terrOf = (code) => {
  if (!code) return "";
  const i = code.toUpperCase().lastIndexOf("-R");
  return i > 0 ? code.slice(0, i) : code;
};

export const daysLabel = (d) =>
  d == null ? "" : d < 0 ? `${-d}d late` : d === 0 ? "due today" : `in ${d}d`;

// ── Collections grouping: flat due-items → one row per parent invoice ───────
export function groupByParent(items) {
  const map = new Map();
  for (const it of items) {
    const key = it.parentInvoiceNum;
    let g = map.get(key);
    if (!g) {
      g = {
        parentInvoiceNum: key, customerName: it.customerName, custNum: it.custNum,
        planCategory: it.planCategory, salesRepCode: it.salesRepCode, salesRepName: it.salesRepName,
        isExternal: !!it.isExternal, count: it.count,
        rem: 0, gross: 0, paidIn: 0, seqs: [], dueDates: new Set(),
        minDays: Infinity, overdue: 0, sent: 0, po: 0, paid: 0, matches: [], items: [],
      };
      map.set(key, g);
    }
    g.rem += remOf(it);
    g.gross += it.amount || 0;
    g.paidIn += it.paidAmount || 0;
    g.seqs.push(it.seq);
    if (it.dueDate) g.dueDates.add(it.dueDate.slice(0, 10));
    if (it.daysUntilDue != null) g.minDays = Math.min(g.minDays, it.daysUntilDue);
    if (it.isOverdue) g.overdue++;
    if (it.sendStatus === "Sent") g.sent++;
    if (it.customerPONumber) g.po++;
    if (isPaidItem(it)) g.paid++;
    g.matches.push(it.matchStatus);
    g.items.push(it);
  }
  const rows = [...map.values()];
  rows.forEach((g) => { if (g.minDays === Infinity) g.minDays = 0; });
  rows.sort((a, b) => a.minDays - b.minDays || b.rem - a.rem);
  return rows;
}

// Installment chip text for a grouped row ("installment 3 of 6", "4 of 9 installments due"…).
export function seqChip(g) {
  const n = g.count || 0;
  if (g.seqs.length === 1) {
    if (n <= 1) return "single payment";
    return `installment ${g.seqs[0]} of ${n}`;
  }
  if (g.seqs.length <= 3) return `installments ${[...g.seqs].sort((a, b) => a - b).join(", ")} of ${n}`;
  return `${g.seqs.length} of ${n} installments due`;
}

// ── Client-side CSV export (BOM + CRLF for Excel) ───────────────────────────
export function exportCsv(filename, header, rows) {
  // csvCell: Excel-formula guard + CSV escaping, shared with the customer page export (Gap 23).
  const body = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
